import { useState, useCallback } from "react";
import type { FormEvent } from "react";
import Modal from "@/components/win95/Modal";
import { useAuth } from "@/context/AuthContext";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthTab = "login" | "signup";

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const { signInWithEmail, signUpWithEmail, signInWithGitHub } = useAuth();

  const [tab, setTab] = useState<AuthTab>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reset = useCallback(() => {
    setEmail("");
    setPassword("");
    setDisplayName("");
    setError(null);
    setSuccess(null);
  }, []);

  const switchTab = useCallback(
    (t: AuthTab) => {
      setTab(t);
      reset();
    },
    [reset],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleLogin = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setLoading(true);

      const err = await signInWithEmail(email.trim(), password);
      setLoading(false);

      if (err) {
        setError(err);
      } else {
        handleClose();
      }
    },
    [email, password, signInWithEmail, handleClose],
  );

  const handleSignup = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setLoading(true);

      if (!displayName.trim()) {
        setError("Digite seu nome.");
        setLoading(false);
        return;
      }
      if (password.length < 8) {
        setError("A senha deve ter pelo menos 8 caracteres.");
        setLoading(false);
        return;
      }

      const err = await signUpWithEmail(email.trim(), password, displayName.trim());
      setLoading(false);

      if (err) {
        setError(err);
      } else {
        setSuccess("Conta criada! Verifique seu e-mail para confirmar.");
      }
    },
    [email, password, displayName, signUpWithEmail],
  );

  const handleGitHub = useCallback(async () => {
    setError(null);
    const err = await signInWithGitHub();
    if (err) setError(err);
  }, [signInWithGitHub]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="🔑 Login — Pro_Gramming">
      <div className="auth-tabs">
        <button
          className={`auth-tab${tab === "login" ? " active" : ""}`}
          onClick={() => switchTab("login")}
        >
          ENTRAR
        </button>
        <button
          className={`auth-tab${tab === "signup" ? " active" : ""}`}
          onClick={() => switchTab("signup")}
        >
          CRIAR CONTA
        </button>
      </div>

      {error && (
        <div className="auth-error">
          <span>✕</span> {error}
        </div>
      )}

      {success && <div className="auth-success">{success}</div>}

      {!success && (
        <form
          className="auth-form"
          onSubmit={tab === "login" ? handleLogin : handleSignup}
        >
          {tab === "signup" && (
            <div className="auth-field">
              <label>NOME</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Seu nome"
                autoComplete="name"
              />
            </div>
          )}

          <div className="auth-field">
            <label>E-MAIL</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              autoComplete="email"
              required
            />
          </div>

          <div className="auth-field">
            <label>SENHA</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete={tab === "login" ? "current-password" : "new-password"}
              required
              minLength={tab === "signup" ? 8 : 6}
            />
          </div>

          <button className="auth-submit" type="submit" disabled={loading}>
            {loading
              ? "AGUARDE..."
              : tab === "login"
                ? "[ ENTRAR ]"
                : "[ CRIAR CONTA ]"}
          </button>

          <div className="auth-divider">— ou —</div>

          <button
            className="auth-github"
            type="button"
            onClick={handleGitHub}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
            </svg>
            ENTRAR COM GITHUB
          </button>
        </form>
      )}
    </Modal>
  );
}
